import { CommonModule, NgClass } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { IonicModule, ModalController, PopoverController } from '@ionic/angular';
import _ from 'lodash';
import { v4 as uuid } from 'uuid';

import { EmojiPickerComponent } from 'src/app/components/emoji-picker/emoji-picker.component';
import { INTEGRATION_TYPES, INTEGRATIONS } from 'src/app/constants';
import { ColonToEmojiPipe } from 'src/pipes/colonToEmoji.pipe';
import { ApiService } from 'src/services/api.service';
import { AuthService } from 'src/services/auth.service';
import { MessageService } from 'src/services/message.service';
import { NangoService } from 'src/services/nango.service';
import { SearchablePopoverService } from 'src/services/searchable-popover.service';

interface FieldMapping {
  externalField: string; // external field NAME
  internalField: string; // Unthread custom field ID
  externalFieldType?: string; // external field TYPE (e.g., 'currency', 'string', 'date', 'reference')
  relationshipName?: string; // for reference fields: the relationship name to query (e.g., 'Owner' for 'OwnerId')
}

@Component({
  standalone: true,
  selector: 'app-edit-integration',
  templateUrl: './integration-input-modal.html',
  styleUrls: ['./integration-input-modal.css'],
  imports: [CommonModule, IonicModule, FormsModule, NgClass, EmojiPickerComponent, ColonToEmojiPipe],
})
export class IntegrationInputModalComponent implements OnInit {
  integration: any;
  integrationSettings: any;
  integrationOutput: any;

  isSavingIntegration = false;
  formSubmitted = false;
  validationErrors: { [key: string]: boolean } = {};

  // Cache for external CRM account fields (e.g., Salesforce fields)
  externalCrmAccountFields: any = null;
  isLoadingExternalFields = false;

  constructor(
    public authService: AuthService,
    private msgSrvc: MessageService,
    private modalCtrl: ModalController,
    private apiService: ApiService,
    private nangoService: NangoService,
    private popoverCtrl: PopoverController,
    private searchablePopoverService: SearchablePopoverService,
  ) {}

  async ngOnInit() {
    this.integrationSettings = this.integrationSettings ?? {};

    // no user input is required. go to registration
    if (!this.integration.userInput) {
      return this.registerIntegration();
    }

    if (!this.integrationSettings.userInput) {
      this.integrationSettings.userInput = {};
    }

    if (!this.integrationSettings.nangoConfig) {
      this.integrationSettings.nangoConfig = {};
    }

    this.integrationSettings.userInput = {
      ...this.integrationSettings.userInput,
      ...this.integrationSettings.nangoConfig,
    };

    this.integrationSettings.userInput = {
      ...this.integration.userInput?.reduce((acc, inputField) => {
        if (inputField['default'] !== undefined) {
          acc[inputField.key] = inputField['default'];
        }

        return acc;
      }, {}),
      ...this.integrationSettings.userInput,
    };

    // Initialize field mappings arrays if needed and hydrate labels
    this.integration.userInput?.forEach((inputField: any) => {
      if (inputField.type === 'fieldMapping') {
        if (!this.integrationSettings.userInput[inputField.key]) {
          this.integrationSettings.userInput[inputField.key] = [];
        }
        // Hydrate labels from tenant custom fields
        this.hydrateFieldMappingLabels(inputField);
      }
    });

    // Pre-fetch external CRM fields for integrations that need them (e.g., Salesforce)
    const needsExternalFields = this.integration.userInput?.some(
      (inputField: any) => inputField.type === 'fieldMapping',
    );
    if (needsExternalFields && this.integration.key === INTEGRATIONS.salesforce.key) {
      await this.loadExternalCrmFields();
    }
  }

  async openEmojiPicker(inputField: any, event: any) {
    const popover = await this.popoverCtrl.create({
      component: EmojiPickerComponent,
      componentProps: {
        callback: (emoji: any) => {
          popover.dismiss();
          let emojiToStore: string;

          if (emoji.custom) {
            emojiToStore = `:${emoji.id}:`;
          } else if (emoji.colons) {
            emojiToStore = emoji.colons;
          } else if (emoji.id) {
            emojiToStore = `:${emoji.id}:`;
          } else {
            // Fallback for unicode emojis
            emojiToStore = emoji.native || emoji.emoji || emoji;
          }

          this.integrationSettings.userInput[inputField.key] = emojiToStore;
        },
      },
      showBackdrop: false,
      cssClass: 'emoji-picker-popover',
      event,
    });

    popover.present();
  }

  isURL(str: string): boolean {
    try {
      new URL(str);
      return true;
    } catch {
      return false;
    }
  }

  onDismissIntegrationInputModal(data: any = null) {
    this.modalCtrl.dismiss(data);
    (this as any).modal?.dismiss(data);
  }

  async onSubmitIntegrationInputModal() {
    this.formSubmitted = true;
    this.validationErrors = {};
    let hasError = false;
    if (this.integration && this.integration.userInput) {
      for (const inputField of this.integration.userInput) {
        if (inputField.required && !this.integrationSettings.userInput[inputField.key]) {
          this.validationErrors[inputField.key] = true;
          hasError = true;
        }

        // Add field mapping validation
        if (inputField.type === 'fieldMapping') {
          const isValid = this.validateFieldMappings(inputField);
          if (!isValid) {
            this.validationErrors[inputField.key] = true;
            hasError = true;
          }
        }
      }
    }
    if (hasError) {
      // Optionally show a toast or message here
      return;
    }
    await this.registerIntegration();
  }

  shouldShowHelper(inputField: any) {
    if (inputField.helper) {
      const value = this.integrationSettings.userInput[inputField.key];
      return !value || (inputField.format === 'snakeCase' && value.includes(' '));
    }

    return false;
  }

  async registerIntegration() {
    this.isSavingIntegration = true;

    try {
      const mergedInput = {
        ...(this.integrationSettings.userInput ?? {}),
        ...(this.integrationSettings.nangoConfig ?? {}),
      };
      let nangoConfig: any;
      let params: any;
      let secrets: any;

      if (!_.isEmpty(mergedInput)) {
        ({ nangoConfig, params, secrets } = Object.keys(mergedInput).reduce(
          (acc, key) => {
            const field = this.integration.userInput?.find((field) => field.key === key);
            let value = mergedInput[key];

            // Strip internalFieldLabel from field as it's redundant and we can load this from the tenant
            if (field?.type === 'fieldMapping' && Array.isArray(value)) {
              value = value.map(({ internalFieldLabel, ...mapping }: any) => mapping);
            }

            if (field?.type === 'secret') {
              acc.secrets[key] = value;
            } else if (field?.isNangoConfig) {
              acc.nangoConfig[key] = value;
            } else {
              acc.params[key] = value;
            }

            return acc;
          },
          { nangoConfig: {}, params: {}, secrets: {} } as any,
        ));
      }

      const nangoIntegrationId = this.integrationSettings.nangoIntegrationId ?? this.integration.key;

      let connectionId: string;

      if (this.integration.isNango) {
        ({ connectionId } = await this.nangoService.authorize(
          nangoIntegrationId,
          nangoConfig,
          this.integration.multipleConnections ? [uuid()] : undefined,
          this.integration.authorizationParams,
        ));
      }

      const result: any = await this.apiService.postPromise(`/integrations/register`, {
        integration: nangoIntegrationId,
        integrationType: this.integration.type,
        isNango: this.integration.isNango,
        connectionId,
        params,
        secrets,
        projectId: this.integration.isProjectSpecific ? this.authService.selectedProject?.id ?? null : undefined,
      });

      this.integrationOutput = result.data;

      await this.authService.loadCompany();

      if (this.integration.type === INTEGRATION_TYPES.KNOWLEDGEBASE) {
        this.msgSrvc.show('Connected! Syncing docs now');
      } else {
        this.msgSrvc.show('Success!');
      }
      this.onDismissIntegrationInputModal({ saved: true, integrationOutput: this.integrationOutput });
    } catch (err: any) {
      this.apiService.postPromise('/log-entries', {
        level: 'error',
        data: {
          message: err.message,
          stack: err.stack,
          code: err.code,
          apiError: err?.error,
        },
      });
      this.msgSrvc.showError(err);
    } finally {
      this.isSavingIntegration = false;
    }
  }

  isFormValid(): boolean {
    if (!this.integration || !this.integration.userInput) return true;
    for (const inputField of this.integration.userInput) {
      if (inputField.required && !this.integrationSettings.userInput[inputField.key]) {
        return false;
      }
    }
    return true;
  }

  addFieldMapping(inputField: any) {
    const key = inputField.key;
    if (!this.integrationSettings.userInput[key]) {
      this.integrationSettings.userInput[key] = [];
    }

    this.integrationSettings.userInput[key].push({
      externalField: '',
      internalField: '',
      externalFieldType: '',
      relationshipName: '',
    });
  }

  hydrateFieldMappingLabels(inputField: any) {
    const key = inputField.key;
    const mappings = this.integrationSettings.userInput[key] || [];
    const customFields = this.authService.tenant.customCustomerFields || [];

    mappings.forEach((mapping: any) => {
      if (mapping.internalField) {
        const field = customFields.find((f) => f.id === mapping.internalField);
        mapping.internalFieldLabel = field?.label || 'Unknown field (may be deleted)';
      }
    });
  }

  removeFieldMapping(inputField: any, index: number) {
    const key = inputField.key;
    this.integrationSettings.userInput[key].splice(index, 1);
  }

  async loadExternalCrmFields() {
    if (this.externalCrmAccountFields) {
      return; // Already loaded
    }

    this.isLoadingExternalFields = true;
    try {
      const response: any = await this.apiService.getPromise('/external-crm/account-fields', {
        type: this.integration.key, // e.g., 'salesforce'
      });
      this.externalCrmAccountFields = response.fields || [];
    } catch (err: any) {
      this.msgSrvc.showError(err);
    } finally {
      this.isLoadingExternalFields = false;
    }
  }

  async openExternalFieldPopover(event: any, inputField: any, index: number) {
    const key = inputField.key;
    const mapping = this.integrationSettings.userInput[key][index];

    // Ensure external fields are loaded
    if (!this.externalCrmAccountFields && !this.isLoadingExternalFields) {
      await this.loadExternalCrmFields();
    }

    // If still loading or failed to load, don't show popover
    if (!this.externalCrmAccountFields) {
      return;
    }

    // Show popover with external fields
    await this.searchablePopoverService.showPopover({
      event,
      selectedItemId: mapping.externalField,
      callback: (_id: string, _fieldLabel: string, selectedField: any) => {
        const key = inputField.key;
        const mapping = this.integrationSettings.userInput[key][index];
        if (mapping) {
          mapping.externalField = selectedField.name;
          mapping.externalFieldType = selectedField.type;
          mapping.relationshipName = selectedField.relationshipName || '';
        }
      },
      loadData: async (searchText: string) => {
        const filtered = this.externalCrmAccountFields.filter(
          (field: any) =>
            field.label.toLowerCase().includes(searchText.toLowerCase()) ||
            field.name.toLowerCase().includes(searchText.toLowerCase()),
        );
        return filtered.map((field: any) => ({
          id: field.name,
          title: `${field.label} (${field.name})`,
          object: field,
        }));
      },
      showSearch: true,
      initialItems: this.externalCrmAccountFields.map((field: any) => ({
        id: field.name,
        title: `${field.label} (${field.name})`,
        object: field,
      })),
    });
  }

  async openInternalFieldPopover(event: any, inputField: any, index: number) {
    const key = inputField.key;
    const mapping = this.integrationSettings.userInput[key][index];

    const popoverType = inputField.fieldMappingConfig.internalFieldPopoverType;

    await this.searchablePopoverService[popoverType]({
      event,
      selectedFieldId: mapping.internalField,
      callback: (id: string, label: string) => {
        const key = inputField.key;
        const mapping = this.integrationSettings.userInput[key][index];
        if (mapping) {
          mapping.internalField = id;
          mapping.internalFieldLabel = label;
        }
      },
    });
  }

  validateFieldMappings(inputField: any): boolean {
    const key = inputField.key;
    const mappings = this.integrationSettings.userInput[key] || [];
    const customFields = this.authService.tenant.customCustomerFields || [];
    const validFieldIds = new Set(customFields.map((f) => f.id));

    // Check for empty fields and validate field existence
    for (const mapping of mappings) {
      if (!mapping.externalField || !mapping.internalField) {
        return false;
      }

      // Validate internal field still exists on tenant
      if (!validFieldIds.has(mapping.internalField)) {
        this.msgSrvc.showError(
          `Custom field "${mapping.internalFieldLabel || 'Unknown'}" no longer exists. Please update your field mappings.`,
        );
        return false;
      }
    }

    // Check for duplicate external field names
    const externalFields = mappings.map((m: FieldMapping) => m.externalField);
    const hasDuplicates = externalFields.length !== new Set(externalFields).size;

    if (hasDuplicates) {
      this.msgSrvc.showError('Field mappings must have unique Salesforce field names.');
      return false;
    }

    return true;
  }
}
